import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../../lib/cn";
import { Label } from "../Label";

type FormApi = {
  handleSubmit: () => Promise<unknown>;
};

export interface FormProps extends Omit<
  React.ComponentProps<"form">,
  "onSubmit"
> {
  form: FormApi;
}

export function Form({ form, ...props }: FormProps) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        void form.handleSubmit();
      }}
      {...props}
    />
  );
}

type FormFieldContextValue = {
  name: string;
  state: {
    meta: {
      errors: readonly unknown[];
      isValid: boolean;
      isTouched: boolean;
    };
  };
};

const FormFieldContext = React.createContext<FormFieldContextValue | null>(
  null,
);

export interface FormFieldProps {
  field: FormFieldContextValue;
  children: React.ReactNode;
}

export function FormField({ field, children }: FormFieldProps) {
  return (
    <FormFieldContext.Provider value={field}>
      {children}
    </FormFieldContext.Provider>
  );
}

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue | null>(null);

export type FormItemProps = React.ComponentProps<"div">;

export function FormItem({ className, id: customId, ...props }: FormItemProps) {
  const generatedId = React.useId();
  const id = customId ?? generatedId;

  return (
    <FormItemContext.Provider value={{ id }}>
      <div className={cn("space-y-2", className)} {...props} />
    </FormItemContext.Provider>
  );
}

function errorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  return "";
}

export function useFormField() {
  const field = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);

  if (!field) {
    throw new Error("useFormField must be used within <FormField>");
  }

  // Whole-object schema revalidates all fields on any change, populating errors
  // for untouched fields; only surface errors after interaction or form submit.
  const isTouched = field.state.meta.isTouched;
  const error = isTouched ? field.state.meta.errors[0] : undefined;
  const id = itemContext?.id ?? field.name;

  return {
    id,
    name: field.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    error,
    isInvalid: isTouched && !field.state.meta.isValid,
    message: errorMessage(error),
  };
}

export type FormLabelProps = React.ComponentProps<typeof Label>;

export function FormLabel({
  className,
  error: customError,
  htmlFor,
  ...props
}: FormLabelProps) {
  const { isInvalid, formItemId } = useFormField();

  return (
    <Label
      className={cn(className)}
      error={customError ?? isInvalid}
      htmlFor={htmlFor ?? formItemId}
      {...props}
    />
  );
}

export type FormControlProps = React.ComponentProps<typeof Slot>;

export function FormControl({
  id,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  ...props
}: FormControlProps) {
  const { isInvalid, formItemId, formDescriptionId, formMessageId } =
    useFormField();
  const invalid = ariaInvalid !== undefined ? ariaInvalid : isInvalid;
  const defaultDescribedBy = isInvalid
    ? `${formDescriptionId} ${formMessageId}`
    : formDescriptionId;

  return (
    <Slot
      id={id ?? formItemId}
      aria-describedby={ariaDescribedby ?? defaultDescribedBy}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
}

export type FormDescriptionProps = React.ComponentProps<"p">;

export function FormDescription({
  className,
  id,
  ...props
}: FormDescriptionProps) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      id={id ?? formDescriptionId}
      className={cn("text-body-s-r text-text-placeholder", className)}
      {...props}
    />
  );
}

export type FormMessageProps = React.ComponentProps<"p">;

export function FormMessage({
  className,
  children,
  id,
  ...props
}: FormMessageProps) {
  const { message, formMessageId } = useFormField();
  const body = message || children;

  if (!body) return null;

  return (
    <p
      id={id ?? formMessageId}
      className={cn(
        "text-body-s-r text-fail-text animate-in fade-in-0 slide-in-from-top-1 duration-140",
        className,
      )}
      {...props}
    >
      {body}
    </p>
  );
}
